import { useAtomCallback } from "jotai/utils";
import { useCallback, useMemo } from "react";

import {
	$queryExecutor,
	$queryState,
	type QueryBuilder,
	type QueryStateEntry,
	createQueryBuilder,
} from "../../internal/request-page";
import { store } from "../../internal/store";

/**
 * Hook that provides a QueryBuilder for executing HTTP requests from code cells.
 *
 * The QueryBuilder returns QueryHandle objects with fetch(), stream(), refetch(), and abort() methods.
 *
 * @param documentId - The document ID to scope queries to
 * @returns QueryBuilder function, or undefined if documentId is not provided
 *
 * @example
 * ```ts
 * const query = useQueryBuilder(documentId);
 * if (query) {
 *   const handle = query({ request: "listPods", vars: { namespace: "default" } });
 *   const response = await handle.fetch();
 * }
 * ```
 */
export const useQueryBuilder = (documentId: string | undefined): QueryBuilder | undefined => {
	const dispatch = useAtomCallback(
		useCallback(
			(_get, set, input: Parameters<ReturnType<typeof $queryExecutor>["write"]>[2]) => {
				if (!documentId) {
					throw new Error("documentId is required for query execution");
				}
				return set($queryExecutor(documentId), input);
			},
			[documentId]
		)
	);

	const getState = useAtomCallback(
		useCallback((get, _set, key: string) => {
			return get($queryState(key));
		}, [])
	);

	const subscribeToState = useCallback(
		(key: string, onChange: (state: QueryStateEntry) => void) => {
			return store.sub($queryState(key), () => {
				onChange(store.get($queryState(key)));
			});
		},
		[]
	);

	return useMemo(() => {
		if (!documentId) {
			return undefined;
		}
		return createQueryBuilder(dispatch, getState, subscribeToState);
	}, [documentId, dispatch, getState, subscribeToState]);
};
